import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { CreditCard, Lock, ArrowLeft, Loader2 } from 'lucide-react';
import { toast } from '../hooks/use-toast';

const API_URL = '';

const CheckoutPage = () => {
  const { cartItems, cartTotal } = useCart();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);

  const [shippingInfo, setShippingInfo] = useState({
    firstName: user?.name?.split(' ')[0] || '',
    lastName: user?.name?.split(' ').slice(1).join(' ') || '',
    email: user?.email || '',
    address: '',
    city: '',
    postcode: '',
    country: 'United Kingdom',
    phone: ''
  });

  const handleShippingChange = (e) => {
    setShippingInfo(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleCheckout = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const res = await fetch(`${API_URL}/api/checkout/create-session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          origin_url: window.location.origin,
          items: cartItems.map(item => ({
            name: item.name,
            price: item.price,
            quantity: item.quantity,
            productId: item.id
          })),
          shipping_info: shippingInfo
        })
      });

      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Checkout failed');
      }

      const data = await res.json();

      if (data.url) {
        window.location.href = data.url;
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (err) {
      toast({ title: 'Payment error', description: err.message, variant: 'destructive' });
      setLoading(false);
    }
  };

  if (cartItems.length === 0) {
    return (
      <div className="bg-[#0a0a0a] min-h-screen pt-28 pb-20">
        <div className="max-w-lg mx-auto px-4 text-center py-20">
          <p className="text-gray-400 mb-6">Your cart is empty.</p>
          <Link to="/shop" className="inline-flex items-center gap-2 bg-[#2EC866] hover:bg-[#25a855] text-white font-semibold px-6 py-3 rounded-lg transition-all duration-300">
            Go Shopping
          </Link>
        </div>
      </div>
    );
  }

  const inputCls = "w-full bg-[#111] border border-gray-800 rounded-lg px-4 py-3 text-white text-sm placeholder:text-gray-600 focus:outline-none focus:border-[#2EC866]/50 transition-colors";

  return (
    <div className="bg-[#0a0a0a] min-h-screen pt-28 pb-20">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <Link to="/cart" className="inline-flex items-center gap-2 text-gray-500 hover:text-white text-sm mb-8 transition-colors">
          <ArrowLeft className="w-4 h-4" /> Back to Cart
        </Link>

        <h1 className="text-3xl font-bold text-white mb-10" style={{ fontFamily: 'Playfair Display, serif' }}>
          Checkout
        </h1>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Shipping Form */}
          <div className="lg:col-span-2">
            <form onSubmit={handleCheckout} className="bg-[#111] border border-gray-800/50 rounded-xl p-6">
              <h3 className="text-white font-semibold text-lg mb-6">Shipping Information</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-gray-400 text-xs mb-1.5 block">First Name *</label>
                  <input data-testid="checkout-firstname" name="firstName" value={shippingInfo.firstName} onChange={handleShippingChange} required className={inputCls} />
                </div>
                <div>
                  <label className="text-gray-400 text-xs mb-1.5 block">Last Name *</label>
                  <input data-testid="checkout-lastname" name="lastName" value={shippingInfo.lastName} onChange={handleShippingChange} required className={inputCls} />
                </div>
                <div className="sm:col-span-2">
                  <label className="text-gray-400 text-xs mb-1.5 block">Email *</label>
                  <input data-testid="checkout-email" name="email" type="email" value={shippingInfo.email} onChange={handleShippingChange} required className={inputCls} />
                </div>
                <div className="sm:col-span-2">
                  <label className="text-gray-400 text-xs mb-1.5 block">Address *</label>
                  <input data-testid="checkout-address" name="address" value={shippingInfo.address} onChange={handleShippingChange} required className={inputCls} />
                </div>
                <div>
                  <label className="text-gray-400 text-xs mb-1.5 block">City *</label>
                  <input data-testid="checkout-city" name="city" value={shippingInfo.city} onChange={handleShippingChange} required className={inputCls} />
                </div>
                <div>
                  <label className="text-gray-400 text-xs mb-1.5 block">Postcode *</label>
                  <input data-testid="checkout-postcode" name="postcode" value={shippingInfo.postcode} onChange={handleShippingChange} required className={inputCls} />
                </div>
                <div>
                  <label className="text-gray-400 text-xs mb-1.5 block">Country *</label>
                  <input data-testid="checkout-country" name="country" value={shippingInfo.country} onChange={handleShippingChange} required className={inputCls} />
                </div>
                <div>
                  <label className="text-gray-400 text-xs mb-1.5 block">Phone</label>
                  <input data-testid="checkout-phone" name="phone" value={shippingInfo.phone} onChange={handleShippingChange} className={inputCls} />
                </div>
              </div>

              {/* Stripe info box */}
              <div className="mt-6 bg-[#0a0a0a] border border-[#2EC866]/20 rounded-lg p-4 flex items-center gap-3">
                <Lock className="w-5 h-5 text-[#2EC866] flex-shrink-0" />
                <div>
                  <p className="text-white text-sm font-medium">Secure Payment via Stripe</p>
                  <p className="text-gray-500 text-xs">You will be redirected to Stripe's secure checkout page to complete your payment.</p>
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                data-testid="checkout-pay-btn"
                className="mt-6 w-full bg-[#2EC866] hover:bg-[#25a855] disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold py-3.5 rounded-lg transition-all duration-300 flex items-center justify-center gap-2"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" /> Redirecting to Stripe...
                  </>
                ) : (
                  <>
                    <CreditCard className="w-5 h-5" /> Pay £{cartTotal.toFixed(2)}
                  </>
                )}
              </button>
            </form>
          </div>

          {/* Order Summary */}
          <div className="bg-[#111] border border-gray-800/50 rounded-xl p-6 h-fit sticky top-28">
            <h3 className="text-white font-bold text-lg mb-4">Order Summary</h3>
            <div className="space-y-3 mb-4">
              {cartItems.map(item => (
                <div key={item.id} className="flex items-center gap-3">
                  <img src={item.image} alt={item.name} className="w-12 h-12 object-cover rounded-lg" />
                  <div className="flex-1 min-w-0">
                    <p className="text-white text-xs truncate">{item.name}</p>
                    <p className="text-gray-500 text-xs">Qty: {item.quantity}</p>
                  </div>
                  <p className="text-white text-sm font-medium">&pound;{(item.price * item.quantity).toFixed(2)}</p>
                </div>
              ))}
            </div>
            <div className="border-t border-gray-800 pt-4 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-400">Subtotal</span>
                <span className="text-white">&pound;{cartTotal.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-400">Shipping</span>
                <span className="text-[#2EC866]">Free</span>
              </div>
              <div className="flex justify-between pt-2 border-t border-gray-800">
                <span className="text-white font-semibold">Total</span>
                <span className="text-white font-bold text-xl">&pound;{cartTotal.toFixed(2)}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CheckoutPage;
